import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { Platform, FlatList, Pressable, Dimensions } from 'react-native';
import { View, Text } from '@constants/Themed';
import Svg, { Line } from 'react-native-svg';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import moment from 'moment';
import Modal from 'react-native-modal';
import { CalendarProvider } from 'react-native-calendars';
import { XStack, YStack, Button, H6 } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import TimeLineItem from './subcomponents/TimeLineItem';
import RangePicker from '@components/Global/DateRangePicker/RangePicker';
import * as Animatable from 'react-native-animatable';
import globalStyles from '@stylesheet';

interface TimelineEntry {
  id: string;
  date: string;
  title?: string;
  description?: string;
  status?: 'completed' | 'pending' | 'failed';
  data?: any;
}

// Legacy interface for backwards compatibility
interface LegacyTimelineItem {
  date: string | Date | number;
  mail?: any[];
  phone?: any[];
  messages?: any[];
  calendar?: any[];
  notes?: any[];
  [key: string]: any;
}

interface TimeLineScrollProps {
  // New modern interface
  entries?: TimelineEntry[];
  onItemSelect?: (item: TimelineEntry, index: number) => void;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  
  // Legacy interface (backwards compatibility)
  history?: LegacyTimelineItem[];
  callBack?: (item: LegacyTimelineItem) => void;
  onSnapToItem?: (index: number) => void;
  initialDates?: string[];
  
  // Common props
  vertical?: boolean;
  hideRange?: boolean;
  initialRange?: { startDate: string; endDate: string };
  showGridLines?: boolean;
  itemWidth?: number;
  itemHeight?: number;
  snapToAlignment?: 'start' | 'center' | 'end';
}

const ITEM_SIZE = 80;
const SCREEN_WIDTH = Dimensions.get('window').width;

const TimeLineScroll: React.FC<TimeLineScrollProps> = ({
  // New interface props
  entries = [],
  onItemSelect,
  onDateRangeChange,
  
  // Legacy interface props
  history = [],
  callBack,
  onSnapToItem,
  initialDates = [],
  
  // Common props
  vertical = false, // Horizontal/inline by default
  hideRange = false,
  initialRange,
  showGridLines = false, // Modern clean look without grid
  itemWidth = 280, // Match modern card width
  itemHeight = 160, // Modern card height
  snapToAlignment = 'start' // Align to start for horizontal scroll
}) => {
  const theme = themeSelector();
  const flatListRef = useRef<FlatList>(null);
  
  // State management
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: initialRange?.startDate 
      ? (initialRange.startDate.includes('T') 
          ? moment(initialRange.startDate).format('YYYY-MM-DD')
          : initialRange.startDate)
      : moment().format('YYYY-MM-DD'),
    endDate: initialRange?.endDate 
      ? (initialRange.endDate.includes('T') 
          ? moment(initialRange.endDate).format('YYYY-MM-DD')
          : initialRange.endDate)
      : moment().format('YYYY-MM-DD')
  });
  const [containerDimensions, setContainerDimensions] = useState({ 
    width: SCREEN_WIDTH, 
    height: 200 
  });

  // Convert legacy data to new format
  const timelineEntries = useMemo(() => {
    // If new format entries are provided, use them
    if (entries.length > 0) return entries;
    
    // Convert legacy history to new format
    if (history.length > 0) {
      return history.map((item, index) => {
        let dateStr: string;
        
        // Handle different date formats
        if (typeof item.date === 'string') {
          // Handle ISO strings or regular date strings
          dateStr = item.date.includes('T') 
            ? moment(item.date).format('YYYY-MM-DD')
            : item.date;
        } else if (item.date instanceof Date) {
          dateStr = moment(item.date).format('YYYY-MM-DD');
        } else if (typeof item.date === 'number') {
          dateStr = moment(item.date).format('YYYY-MM-DD');
        } else {
          dateStr = moment().format('YYYY-MM-DD');
        }

        // Determine status based on data presence
        let status: 'completed' | 'pending' | 'failed' = 'pending';
        const hasData = (item.mail?.length || 0) + (item.phone?.length || 0) + 
                       (item.messages?.length || 0) + (item.calendar?.length || 0) + 
                       (item.notes?.length || 0) + (item.whatsapp?.length || 0) > 0;
        
        if (hasData) {
          status = 'completed';
        }

        return {
          id: `legacy-${index}`,
          date: dateStr,
          title: `Entry ${index + 1}`,
          status,
          data: item // Keep original data for legacy callbacks
        };
      });
    }
    
    // Use initialDates if provided
    if (initialDates.length > 0) {
      return initialDates.map((date, index) => ({
        id: `initial-${index}`,
        date: typeof date === 'string' ? date : moment().subtract(index, 'days').format('YYYY-MM-DD'),
        title: `Day ${index + 1}`,
        status: 'pending' as const
      }));
    }
    
    // Generate default entries for the last 30 days
    return Array.from({ length: 30 }, (_, index) => ({
      id: `entry-${index}`,
      date: moment().subtract(29 - index, 'days').format('YYYY-MM-DD'),
      title: `Day ${index + 1}`,
      status: Math.random() > 0.3 ? 'completed' : 'pending' as const
    }));
  }, [entries, history, initialDates]);

  // Scroll handlers
  const handleItemSelect = useCallback((item: TimelineEntry, index: number) => {
    setActiveIndex(index);
    
    // Call new interface callback
    onItemSelect?.(item, index);
    
    // Call legacy interface callbacks
    if (callBack && item.data) {
      callBack(item.data);
    }
    
    if (onSnapToItem) {
      onSnapToItem(index);
    }
    
    // Scroll to selected item
    flatListRef.current?.scrollToIndex({
      index,
      animated: true,
      viewPosition: snapToAlignment === 'center' ? 0.5 : snapToAlignment === 'end' ? 1 : 0
    });
  }, [onItemSelect, callBack, onSnapToItem, snapToAlignment]);

  const handleScrollToIndex = useCallback((index: number) => {
    if (index >= 0 && index < timelineEntries.length) {
      flatListRef.current?.scrollToIndex({
        index,
        animated: true,
        viewPosition: 0.5
      });
      setActiveIndex(index);
    }
  }, [timelineEntries.length]);

  const navigateNext = useCallback(() => {
    const nextIndex = Math.min(activeIndex + 1, timelineEntries.length - 1);
    handleScrollToIndex(nextIndex);
  }, [activeIndex, handleScrollToIndex, timelineEntries.length]);

  const navigatePrevious = useCallback(() => {
    const prevIndex = Math.max(activeIndex - 1, 0);
    handleScrollToIndex(prevIndex);
  }, [activeIndex, handleScrollToIndex]);

  // Date range handling
  const handleDateChange = useCallback((dateRange: { startDate: Date; endDate: Date }) => {
    const newRange = { 
      startDate: moment(dateRange.startDate).format('YYYY-MM-DD'), 
      endDate: moment(dateRange.endDate).format('YYYY-MM-DD') 
    };
    setSelectedDateRange(newRange);
    onDateRangeChange?.(newRange.startDate, newRange.endDate);
  }, [onDateRangeChange]);

  // Grid line generation
  const generateGridLines = useCallback(() => {
    if (!showGridLines) return null;
    
    const lines = [];
    const { width, height } = containerDimensions;
    const gridSpacing = 20;
    
    if (vertical) {
      // Horizontal lines for vertical timeline
      for (let i = 0; i <= height; i += gridSpacing) {
        lines.push(
          <Line
            key={`grid-h-${i}`}
            x1={0}
            y1={i}
            x2={width}
            y2={i}
            stroke={theme ? '#00000010' : '#ffffff15'}
            strokeWidth={0.5}
          />
        );
      }
    } else {
      // Vertical lines for horizontal timeline
      for (let i = 0; i <= width; i += gridSpacing) {
        lines.push(
          <Line
            key={`grid-v-${i}`}
            x1={i}
            y1={0}
            x2={i}
            y2={height}
            stroke={theme ? '#00000010' : '#ffffff15'}
            strokeWidth={0.5}
          />
        );
      }
    }
    
    return lines;
  }, [showGridLines, containerDimensions, theme, vertical]);

  // Scroll to today on mount
  useEffect(() => {
    const todayIndex = timelineEntries.findIndex(entry => 
      moment(entry.date).isSame(moment(), 'day')
    );
    if (todayIndex >= 0) {
      setTimeout(() => handleScrollToIndex(todayIndex), 100);
    }
  }, [timelineEntries, handleScrollToIndex]);

  const getItemLayout = useCallback((data: any, index: number) => ({
    length: vertical ? itemHeight : itemWidth,
    offset: (vertical ? itemHeight : itemWidth) * index,
    index
  }), [vertical, itemWidth, itemHeight]);

  const onViewableItemsChanged = useCallback(({ viewableItems }) => {
    if (viewableItems.length > 0) {
      const centerItem = viewableItems[Math.floor(viewableItems.length / 2)];
      if (centerItem?.index !== undefined) {
        setActiveIndex(centerItem.index);
      }
    }
  }, []);

  return (
    <YStack flex={1} gap={20} style={globalStyles.modernMainContent}>
      {/* Calendar Modal */}
      <Modal
        isVisible={showCalendar}
        onBackdropPress={() => setShowCalendar(false)}
        style={{
          margin: 20,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <View style={Platform.OS === 'web'
          ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), width: '90%', maxWidth: 400 }
          : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { width: '90%', maxWidth: 400 }]
        }>
          <CalendarProvider date={selectedDateRange.startDate}>
            {/* Calendar content can be added here */}
          </CalendarProvider>
          <Button
            onPress={() => setShowCalendar(false)}
            marginTop={16}
          >
            Close
          </Button>
        </View>
      </Modal>

      {/* Date Range Picker */}
      {!hideRange && (
        <Animatable.View 
          animation="fadeInDown" 
          duration={600}
          style={Platform.OS === 'web'
            ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark) }
            : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark]
          }
        >
          <RangePicker
            noIcons
            dateCallback={handleDateChange}
            startDate={moment(selectedDateRange.startDate).toDate()}
            endDate={moment(selectedDateRange.endDate).toDate()}
          />
        </Animatable.View>
      )}

      {/* Navigation Controls */}
      <View style={Platform.OS === 'web'
        ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark) }
        : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark]
      }>
        <XStack justifyContent="space-between" alignItems="center" padding={8}>
          <Pressable
            onPress={navigatePrevious}
            disabled={activeIndex === 0}
            style={Platform.OS === 'web'
              ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), opacity: activeIndex === 0 ? 0.5 : 1 }
              : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { opacity: activeIndex === 0 ? 0.5 : 1 }]
            }
          >
            <AntDesign name="left" size={20} color="white" />
          </Pressable>

          <Pressable onPress={() => setShowCalendar(true)}>
            <XStack alignItems="center" gap={8}>
              <MaterialIcons
                name="calendar-today"
                size={20} 
                color={theme ? '#3b82f6' : '#60a5fa'}
              />
              <Text style={{
                color: theme ? '#1e293b' : '#f1f5f9',
                fontSize: 16,
                fontWeight: '600'
              }}>
                {moment(timelineEntries[activeIndex]?.date).format('MMM DD, YYYY')}
              </Text>
            </XStack>
          </Pressable>

          <Pressable
            onPress={navigateNext}
            disabled={activeIndex === timelineEntries.length - 1}
            style={Platform.OS === 'web'
              ? { ...globalStyles.modernActionButton, ...(theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark), opacity: activeIndex === timelineEntries.length - 1 ? 0.5 : 1 }
              : [globalStyles.modernActionButton, theme ? globalStyles.modernActionBlue : globalStyles.modernActionBlueDark, { opacity: activeIndex === timelineEntries.length - 1 ? 0.5 : 1 }]
            }
          >
            <AntDesign name="right" size={20} color="white" />
          </Pressable>
        </XStack>
      </View>

      {/* Timeline Container */}
      <View
        style={Platform.OS === 'web'
          ? { ...globalStyles.modernCard, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), flex: 1, overflow: 'hidden' }
          : [globalStyles.modernCard, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { flex: 1, overflow: 'hidden' }]
        }
        onLayout={(event) => {
          setContainerDimensions({
            width: event.nativeEvent.layout.width,
            height: event.nativeEvent.layout.height
          });
        }}
      >
        {/* Grid Lines Background */}
        {showGridLines && (
          <Svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none'
            }}
            width={containerDimensions.width}
            height={containerDimensions.height}
          >
            {generateGridLines()}
          </Svg>
        )}

        {/* Timeline List */}
        <FlatList
          ref={flatListRef}
          data={timelineEntries}
          horizontal={!vertical}
          keyExtractor={(item) => item.id}
          getItemLayout={getItemLayout}
          showsHorizontalScrollIndicator={false}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            paddingVertical: 16,
            paddingHorizontal: 16,
            gap: 16
          }}
          renderItem={({ item, index }) => (
            <TimeLineItem
              item={item}
              index={index}
              isActive={index === activeIndex}
              onPress={() => handleItemSelect(item, index)}
              vertical={vertical}
              width={itemWidth}
              height={itemHeight}
            />
          )}
          snapToInterval={vertical ? itemHeight + 16 : itemWidth + 16}
          snapToAlignment={snapToAlignment}
          decelerationRate="fast"
          onViewableItemsChanged={onViewableItemsChanged}
          viewabilityConfig={{
            itemVisiblePercentThreshold: 50
          }}
          initialScrollIndex={0}
          onScrollToIndexFailed={(info) => {
            // Handle scroll failure gracefully
            setTimeout(() => {
              flatListRef.current?.scrollToIndex({
                index: Math.min(info.index, timelineEntries.length - 1),
                animated: false
              });
            }, 100);
          }}
        />
      </View>

      {/* Timeline Info */}
      <Animatable.View 
        animation="fadeInUp" 
        duration={600}
        style={Platform.OS === 'web'
          ? { ...globalStyles.modernCardSmall, ...(theme ? globalStyles.modernBgLight : globalStyles.modernBgDark), alignItems: 'center' }
          : [globalStyles.modernCardSmall, theme ? globalStyles.modernBgLight : globalStyles.modernBgDark, { alignItems: 'center' }]
        }
      >
        <Text style={{
          fontSize: 14,
          color: theme ? '#64748b' : '#94a3b8',
          textAlign: 'center',
          fontWeight: '500'
        }}>
          {activeIndex + 1} of {timelineEntries.length} entries
        </Text>
      </Animatable.View>
    </YStack>
  );
};

export default TimeLineScroll;
